import React, { useState } from "react";
import RequestWorkflowSection from "./RequestWorkflowSection";
import QuoteDetails from "./components/QuoteDetails";
import PaymentDetails from "./components/PaymentDetails";
import RequestInfoSection from "./RequestInfoSection";
import { RequestActivityHistory } from "./components/RequestActivityHistory";
import FileUpload from "../DocRepo/FileUpload";
import CancelRequestProcessor from "./components/Cancellation/CancelReqeustProcessor";
import { InfoCard } from "@/utils/infoCard";
import { formatDate } from "@/utils/dateutil";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { buttonLink, buttonPrimary } from "@/styles/buttons";
import { serviceMeta } from "@/config/serviceConfig";
import { Eye, Pencil } from "lucide-react";
import ProfileTabs, { ProfileTab } from "../../modules/profile/ProfileTabs";

interface Props {
  isCancellationFlow: boolean;
  isPaymentComplete: boolean;
  loadingQuote: boolean;
  quote: any;
  paymentStatus: string;
  adminId: number;
  totalPaid: number;
  totalPayable: number;
  payments: any[];
  role: "Admin" | "User";
  requestId: string;
  details: any;
  onResumeChange: (url: string | null) => void;
  onJDChange: (url: string | null) => void;
  onReportsChange: (reports: any[]) => void;
  onRefresh: () => void;
}
const ActionButton = ({
  children,
  onClick,
  color = "hover:bg-slate-100",
}: any) => (
  <button
    onClick={onClick}
    className={`w-10 h-10 rounded-full bg-white shadow hover:shadow-lg transition-all ${color} flex items-center justify-center`}
  >
    {children}
  </button>
);

const ServiceRequestMainContent: React.FC<Props> = ({
  isCancellationFlow,
  isPaymentComplete,
  loadingQuote,
  quote,
  paymentStatus,
  adminId,
  totalPaid,
  totalPayable,
  payments,
  role,
  requestId,
  details,
  onResumeChange,
  onJDChange,
  onReportsChange,
  onRefresh
}) => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<
    "actions" | "reports" | "payment" | "history"
  >("actions");

  const requestTabs: ProfileTab[] = [
  {
    id: "actions",
    label: "Actions",
  },
  {
    id: "payment",
    label: "Payment Details",
  },
  {
    id: "reports",
    label: "Reports",
  },
  {
    id: "history",
    label: "Activity History",
  },
];
  
  const handleRequestEdit = () => {
    if (role === "Admin") {
      navigate(`/admin/edit/${requestId}`);
    } else {
      navigate(`/edit/${requestId}`);
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left Column: 30% */}
        <div className="w-full md:basis-[30%]">



          <div
            className={`
            rounded-3xl
            shadow-lg
            bg-white
            hover:shadow-2xl
            hover:-translate-y-1
            transition-all
            p-6
            flex
            flex-col
          `}
          >
            <div className="relative group">
              <button className={buttonLink} onClick={() => navigate("/dashboard")}>Back</button>
            </div>
            <div className="mt-6 space-y-4 text-base bg-green">
              <div className="flex justify-between items-center mb-5">


                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white">
                    {serviceMeta[details?.service_short_name]?.badge}
                  </div>
                  <div className="text-lg font-bold">
                    {details.form_data?.Request?.["Request Details"]?.evaluation_type ?? "—"}
                  </div>
                  <div className="relative group">
                  <ActionButton onClick={handleRequestEdit}>
                    <Pencil size={18} />
                  </ActionButton>
                    <span className="absolute bottom-full mb-1 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1">
                      Edit
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-1">
            <div className="text-sm"><span className="font-bold">Request #</span>: {details.id}</div>
            <div className="text-sm"><span className="font-bold">Experience (Years)</span>: {details.form_data?.Request?.["Requestor Details"]?.total_experience ?? "—"}</div>
            <div className="text-sm"><span className="font-bold">Skills</span>: {details?.skills}</div>
            </div>

            <div className="p-1">
              <div className="text-sm"><span className="font-bold">Status</span>: {details?.request_status_value} / {details?.request_sub_status_value}</div>
            </div>
            <div className="p-1">
              <div className="text-sm"><span className="font-bold">Created For</span></div>
              <div className="text-sm"><span className="font-bold">Name</span>: {details.form_data?.Request?.["Requestor Details"]?.name ?? "—"}</div>
              <div className="text-sm"><span className="font-bold">Email</span>: {details?.requestor_email ? details?.requestor_email : details.form_data?.Request?.["Requestor Details"]?.email }</div>
              <div className="text-sm"><span className="font-bold">Mobile</span>: {details?.requestor_mobile ? details?.requestor_mobile : details.form_data?.Request?.["Requestor Details"]?.phone  }</div>
              <div className="text-sm"><span className="font-bold">Gender</span>: {details.form_data?.Request?.["Requestor Details"]?.gender ?? "—"}</div>
            </div>
              <div className="p-1">
              <div className="text-sm"><span className="font-bold">Industry</span>: {details?.industry_name}</div>
              <div className="text-sm"><span className="font-bold">Domain</span>: {details?.domain_name}</div>
              <div className="text-sm"><span className="font-bold">Job Title</span>: {details?.role_name}</div>
            </div>
            <div className="p-1">
              <div className="text-sm"><span className="font-bold">Employer</span>: {details.form_data?.Request?.["Requestor Details"]?.employer ?? "—"}</div>
              <div className="text-sm"><span className="font-bold">Employment Status</span>: {details.form_data?.Request?.["Requestor Details"]?.other_employment_status ?? "—"}</div>
            </div>
            <div className="p-1">
              <div className="text-sm"><span className="font-bold">Address</span></div>
              <div>{details.form_data?.Request?.["Requestor Details"]?.address ?? "—"}</div>
              <div>{details.form_data?.Request?.["Requestor Details"]?.city ?? "—"} {details.form_data?.Request?.["Requestor Details"]?.postal_code ?? "—"}</div>
              <div>{details.form_data?.Request?.["Requestor Details"]?.state ?? "—"} {details.form_data?.Request?.["Requestor Details"]?.country ?? "—"}</div>
            </div>
            <div className="p-1">
              <div className="text-sm"><span className="font-bold">Created By</span>: {details?.created_by_id_name}</div>
              <div className="text-sm"><span className="font-bold">Created On</span>: {formatDate(details?.created_date)}</div>
            </div>
          </div>
        </div>
        {/* Right Column: 70% */}
<div className="w-full md:basis-[70%]">
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
<ProfileTabs
  tabs={requestTabs}
  activeTab={activeTab}
  onChange={(tabId) =>
    setActiveTab(
      tabId as
        | "actions"
        | "payment"
        | "reports"
        | "history"
    )
  }
/>

    <div className="p-6">
      {/* Actions */}
      {activeTab === "actions" && (
        <div className="space-y-6">
          {!isCancellationFlow ? (
            <>
              {!isPaymentComplete ? (
                loadingQuote ? (
                  <p className="text-sm text-slate-500">
                    Loading quote...
                  </p>
                ) : !quote ? (
                  <p className="text-sm text-slate-500">
                    No quote available.
                  </p>
                ) : (
                  <QuoteDetails
                    quote={quote}
                    role={role}
                    status={paymentStatus}
                    serviceRequestId={requestId}
                  />
                )
              ) : (
                <RequestWorkflowSection
                  serviceRequestId={requestId}
                  role={role}
                  resumeUrl={
                    details.form_data?.Resume?.resume_url ||
                    null
                  }
                  jdUrl={
                    details.form_data?.JobDescription?.jd_url ||
                    null
                  }
                  reports={
                    details.form_data?.Reports || []
                  }
                  onResumeChange={onResumeChange}
                  onJDChange={onJDChange}
                  onReportsChange={onReportsChange}
                  onRefresh={onRefresh}
                />
              )}
            </>
          ) : (
            role === "Admin" && (
              <div>
                <h2 className="mb-4 text-lg font-semibold text-amber-500">
                  Cancellation Workflow
                </h2>

                <CancelRequestProcessor
                  requestId={details.id}
                  cancellationReason={details.notes}
                  requestStatus={
                    details.request_status_value ?? ""
                  }
                  requestSubStatus={
                    details.request_sub_status_value ?? ""
                  }
                  paymentInfo={{
                    status:
                      paymentStatus ?? "UNKNOWN",
                    totalPaid: totalPaid ?? 0,
                    totalPayable:
                      totalPayable ?? 0,
                    payments: payments ?? [],
                  }}
                  adminId={adminId}
                  onRefresh={onRefresh}
                />
              </div>
            )
          )}
        </div>
      )}

      {/* Request Details */}
      {/* {activeTab === "details" && (
        <RequestInfoSection details={details} />
      )} */}

      {/* Payment Details */}
      {activeTab === "payment" && (
        <PaymentDetails
          payments={payments}
          quote={quote}
          paymentSummary={
            quote?.payment_summary
          }
        />
      )}

      {/* Reports */}
      {activeTab === "reports" && (
        <>
          {!isPaymentComplete ? (
            <p className="text-sm text-slate-500">
              Reports not available yet.
            </p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {role === "Admin" && (
                <FileUpload
                  collection_type="service_request"
                  collection_ref={requestId}
                  repo_type="REPORTS"
                  role={role}
                  label="Reports"
                  multiple
                  files={
                    details.form_data?.Reports ||
                    []
                  }
                  onChange={onReportsChange}
                  onRefresh={onRefresh}
                />
              )}

              {role === "User" &&
                (details.form_data?.Reports
                  ?.length ? (
                  <FileUpload
                    collection_type="service_request"
                    collection_ref={requestId}
                    repo_type="REPORTS"
                    role={role}
                    label="Reports"
                    multiple={false}
                    files={
                      details.form_data
                        ?.Reports || []
                    }
                    onChange={() => {}}
                  />
                ) : (
                  <p className="p-6 text-sm text-slate-500">
                    No reports uploaded yet.
                  </p>
                ))}
            </div>
          )}
        </>
      )}

      {/* Activity History */}
      {activeTab === "history" && (
        <RequestActivityHistory
          serviceRequestId={details.id}
        />
      )}
    </div>
  </div>
</div>
      </div>
    </div>
  );
};

export default ServiceRequestMainContent;
